import { type FC, useEffect, useState } from 'react';

import { useCentralizedProviderData } from '@crehana/auth';
import { CREHANA_BRANDING } from '@crehana/constants/branding';
import CrehanaLogoIso from '@crehana/icons/CrehanaLogoIso';
import { useOrganizationBranding } from '@crehana/layouts/organization-branding-provider';
import { CustomNextImage as Image } from '@crehana/next';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { IconButton } from '@crehana/ui';
import Skeleton from '@crehana/ui/dist/Skeleton';
import { getImgxUrl } from '@crehana/utils';

import PoweredBy from '@/shared/components/PoweredBy';

import { GlobalStyles } from './B2BOrgLayout.styles';
import type { B2BOrgLayoutProps } from './types';

const B2BOrgLayout: FC<React.PropsWithChildren<B2BOrgLayoutProps>> = ({
  orgInfo,
  withoutLogo,
  withFooter = false,
  onBack,
  children,
}) => {
  const { heroUrl, logoUrl, loadingBrandingData } = useGetOrganizationBranding({
    learningLogoUrl: orgInfo.logoThumbnail,
    learningHeroUrl: orgInfo.loginImage,
  });

  return (
    <div className="relative w-full h-full bg-white">
      <GlobalStyles
        primaryColor={orgInfo.lookAndFeel?.primary?.main || undefined}
      />

      <div className="relative flex flex-wrap items-start justify-center w-full min-h-screen tw-pt-4.2 md:pt-32 sm:pt-64 md:pb-20">
        <section className="cui-container">
          {/* Figma composition (nodes 6194:3100 / 6270:930): a CENTERED content-sized
              block [cover 529px][gap 73px][content 480px], NOT a full-width 50/50 grid.
              The old md:grid-cols-2 made each column 50%, and since the 529px cover is
              narrower than its half, the empty right-of-cover space doubled the visual
              gap. Fixed tracks + justify-center give the real gap; items-start top-aligns
              both columns so the cover can't stretch to the form's height. The gap uses
              the CDS semantic token md:tw-cds-gap-x-10 (space-10 = 72px; 1px off 73, ok). */}
          <section className="tw-container tw-grid-cols-1 sm:tw-grid-cols-1 md:tw-grid-cols-[529px_480px] lg:tw-grid-cols-[529px_480px] md:tw-cds-gap-x-10 md:tw-justify-center md:tw-items-start w-full max-w-full">
            <div className="hidden md:flex lg:flex flex-col justify-start">
              {/* FIXED 529×580 (Figma cover). Was `h-full` → the cover stretched to the
                  row height, which the taller form column inflated past 580px. Pinning
                  the height + overflow-hidden decouples it: the image stays 580px tall on
                  every state (short token-invalid AND tall form) and top-aligns. */}
              <div
                className="flex justify-start tw-rounded-[12px] tw-overflow-hidden"
                css={{ width: 529, height: 580 }}
              >
                {loadingBrandingData ? (
                  <Skeleton
                    css={{ width: 529, height: 580 }}
                    isAnimated={true}
                    className="tw-rounded-[12px]"
                  />
                ) : (
                  <Image
                    // `cover` fills the fixed 529×580 rounded frame edge-to-edge (the
                    // wrapper clips to the 12px curve).
                    className="tw-rounded-[12px]"
                    src={heroUrl}
                    alt="default_login_image"
                    width={529}
                    height={580}
                    objectFit="cover"
                  />
                )}
              </div>
              <PoweredBy />
            </div>
            {/* No md padding: the old md:tw-p-0.8 (8px) pushed the content 8px below the
                cover's top and widened the section gap to 81px. Removing it top-aligns
                the two columns and makes the gap the real 73px (grid gap). */}
            <div>
              <div className="relative">
                {onBack && (
                  <IconButton
                    className="absolute left-0 -ml-72 hidden sm:block"
                    arrow="left"
                    onClick={onBack}
                  />
                )}
                <div className="mb-56 md:m-0">
                  {/* Figma: logo→title gap = 32px (right column gap-[32px], node
                      6194:3105). `md:mb-32` (standard scale = 32px here) is used instead
                      of a `tw-`-prefixed class so it lives in the same stylesheet as
                      `sm:mb-56` and — md ordered after sm — actually wins at md+ (the
                      `tw-mb-*` variant was being overridden by `sm:mb-56` = 56px). */}
                  {!withoutLogo && (
                    <div className="tw-flex tw-items-center tw-justify-center md:tw-block tw-mt-7.2 tw-mb-3.2 sm:mb-56 md:mt-0 md:mb-32">
                      {logoUrl ? (
                        // legacy code, will be fixed in a future rewrite
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          // Figma org logo = 40px tall (node 6194:3106). Arbitrary value
                          // `tw-h-[40px]` — the integer step `tw-h-4` does NOT resolve in
                          // this repo's custom spacing scale (steps are like 4.4=44px), so
                          // it left the img unconstrained → rendered at natural size.
                          className="tw-h-[40px] tw-w-auto"
                          alt={orgInfo.name}
                          src={getImgxUrl(logoUrl, {
                            auto: 'format',
                            h: 40,
                            w: 'auto',
                            dpr: 2,
                          })}
                        />
                      ) : (
                        <CrehanaLogoIso
                          size={18}
                          ignoreColor
                          className="fill-current text-primary-main"
                        />
                      )}
                    </div>
                  )}

                  {/* Figma right content column = 480px wide (token-invalid + success
                      both measure 480). md:py-36 (top padding) inflated the logo→title
                      gap, so it's removed on md+; the 32px logo margin now owns that gap. */}
                  <div className="bg-white md:bg-transparent md:tw-py-0 px-0 rounded-10 md:tw-max-w-[480px]">
                    {children}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </section>

        {/* Footer */}
        {withFooter && (
          <div className="tw-flex md:tw-hidden tw-justify-center tw-items-center tw-mt-auto tw-w-full tw-h-7.2 tw-bg-neutral-light-200 tw-text-neutral-light-500 tw-py-1.6 tw-gap-0.8">
            <span className="tw-font-body5">Powered by</span>
            <CrehanaLogoIso size={18} ignoreColor className="fill-current" />
          </div>
        )}
      </div>
    </div>
  );
};

type TUseGetOrganizationBrandingOptions = {
  learningLogoUrl?: string | null;
  learningHeroUrl?: string | null;
};

const useGetOrganizationBranding = ({
  learningLogoUrl,
  learningHeroUrl,
}: TUseGetOrganizationBrandingOptions) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<{
    logoUrl?: string | null;
    heroUrl?: string | null;
  }>();

  const { currentPageOrganization, loading: loadingCentralizedProviderData } =
    useCentralizedProviderData();
  const { branding } = useOrganizationBranding();

  useEffect(() => {
    if (loadingCentralizedProviderData) {
      return;
    }

    if (currentPageOrganization?.id) {
      setData({
        logoUrl: branding.imageLogo.absoluteUrl,
        heroUrl: branding.imagePageLoginHero.absoluteUrl,
      });
      setLoading(false);
      return;
    }

    // solo learning organizations
    setData({
      logoUrl: learningLogoUrl,
      heroUrl: learningHeroUrl
        ? `${learningHeroUrl}?w=564&h=580&auto=format&fit=contain`
        : null,
    });
    setLoading(false);
  }, [
    loadingCentralizedProviderData,
    branding,
    currentPageOrganization,
    learningLogoUrl,
    learningHeroUrl,
  ]);

  return {
    loadingBrandingData: loading,
    logoUrl: data?.logoUrl || CREHANA_BRANDING.imageLogo.absoluteUrl,
    heroUrl: data?.heroUrl || CREHANA_BRANDING.imagePageLoginHero.absoluteUrl,
  };
};

export default B2BOrgLayout;
